import React, {useEffect, useState} from 'react';
import {Card, CardContent, Button, Typography, Box, CircularProgress, LinearProgress} from '@mui/material';
import Icon1 from '../../../assets/images/DQProImages/IconImg1.png';
import Icon2 from '../../../assets/images/DQProImages/IconImg2.png';
import Icon4 from '../../../assets/images/DQProImages/IconImg4.png';
import useStyles from '../../../assets/styles/ProgramCreatorStyles';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {
    buildLearningProgramService, generateLearningProgramContentService,
    getInsiderProgramService,
    getLearningProgramPreviewStatusService, getLearningProgramStatusService
} from "../../../services/learning_programs.service";
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import {ENABLE_EARLY_PREVIEW} from "../../../utils/constants";
import {toastWarning} from "../../../utils/utils";

const OUTLINE_STEPS = [
    "Generating program modules and descriptions.",
    "Generating program units and descriptions.",
    "Generating program sessions and descriptions.",
    "Generating program session learning objectives.",
    "Applying learner context.",
    "Organizing program outline.",
    "Mapping with DQ Codes.",
    "Identifying related frameworks."
];

const CONTENT_GENERATION_STEPS = [
    "Generating learning content",      // 0%
    "Generating images",                // 20%
    "Generating activities",            // 40%
    "Generating videos",                // 60%
    "Organizing program sessions",      // 80%
    "Complete"                          // 100%
];

const STATUS_TO_STEP_INDEX = {
    1: 1, // steps 0-1 → "modules" + "units"
    2: 3, // steps 2-3 → "sessions" + "learning objectives"
    3: 5, // steps 4-5 → "learner context" + "organizing outline"
    4: 6, // step 6 → "mapping with DQ Codes"
    5: 7, // step 7 → "identifying related frameworks"
};

function deriveStepIndexFromStatus(progressStatus) {
    if (!progressStatus || progressStatus < 1) return 0;
    return STATUS_TO_STEP_INDEX[Math.min(progressStatus, 5)] ?? 0;
}

function derivePercentFromStatus(isReady, progressStatus) {
    if (isReady) return 100;
    // Spread 1..5 across 95% (reserve the last 5% for the final "ready" flip)
    if (!progressStatus) return 0; // not started yet
    const clamped = Math.max(1, Math.min(progressStatus, 5));
    return Math.round((clamped / 5) * 95);
}

/**
 * Calculate content generation progress based on session completion status
 * @param {Object} contentStatus - Object with session keys and their status/ProgressStatus
 * @returns {Object} - { completedCount, totalCount, progressPercent, stepIndex, currentSession, sessionProgress }
 */
function calculateContentProgress(contentStatus) {
    if (!contentStatus || typeof contentStatus !== 'object') {
        return {
            completedCount: 0,
            totalCount: 0,
            progressPercent: 0,
            stepIndex: 0,
            currentSession: 0,
            sessionProgress: 0
        };
    }

    const sessions = Object.keys(contentStatus);
    const totalCount = sessions.length;

    // Handle both old format (string) and new format (object with status and ProgressStatus)
    let completedCount = 0;
    let currentSessionIndex = 0;
    let currentSessionProgress = 0;

    sessions.forEach((key, index) => {
        const sessionData = contentStatus[key];

        // Check if new format (object) or old format (string)
        const status = typeof sessionData === 'object' ? sessionData.status : sessionData;
        const progressStatus = typeof sessionData === 'object' ? sessionData.ProgressStatus : null;

        if (status === 'done') {
            completedCount++;
        } else if (status === 'in_progress' && progressStatus) {
            // This is the current session being worked on
            currentSessionIndex = index + 1;
            // ProgressStatus is 1-6, convert to percentage (inverse scale)
            // 1 = just started (~17%), 6 = almost done (~100%)
            currentSessionProgress = Math.round((progressStatus / 6) * 100);
        }
    });

    if (totalCount === 0) {
        return {
            completedCount: 0,
            totalCount: 0,
            progressPercent: 0,
            stepIndex: 0,
            currentSession: 0,
            sessionProgress: 0
        };
    }

    // Calculate overall progress including partial progress of current session
    const overallProgress = completedCount + (currentSessionProgress / 100);
    const progressPercent = Math.round((overallProgress / totalCount) * 100);

    // Map progress to steps: 0-20%, 21-40%, 41-60%, 61-80%, 81-99%, 100%
    let stepIndex;
    if (progressPercent === 100) {
        stepIndex = 5; // Complete
    } else if (progressPercent >= 80) {
        stepIndex = 4; // Organizing program sessions
    } else if (progressPercent >= 60) {
        stepIndex = 3; // Generating videos
    } else if (progressPercent >= 40) {
        stepIndex = 2; // Generating activities
    } else if (progressPercent >= 20) {
        stepIndex = 1; // Generating images
    } else {
        stepIndex = 0; // Generating learning content
    }

    return {
        completedCount,
        totalCount,
        progressPercent,
        stepIndex,
        currentSession: currentSessionIndex,
        sessionProgress: currentSessionProgress
    };
}

const DQProProgDashboardPage = () => {
    const classes = useStyles();
    const location = useLocation();
    const history = useHistory();
    const { code } = useParams();
    const [programData, setProgramData] = useState(location.state?.assessment || null);
    const [loading, setLoading] = useState(!location.state?.assessment);
    const [outlineStatus, setOutlineStatus] = useState({
        is_outline_ready: null,
        generation_initiated: null,
        failed: false,
        failure_reason: '',
        ml_server_resp: null
    });
    const [previewEnabled, setPreviewEnabled] = useState(false);
    const [dqcoList, setDqcoList] = useState(location.state?.dqcoList || []);
    const [contentStatus, setContentStatus] = useState({
        ml_server_resp: null
    });

    // local UI progress for outline generation
    const [outlineStepIndex, setOutlineStepIndex] = useState(0);
    const [outlineProgress, setOutlineProgress] = useState(0);

    // Content generation progress tracking
    const [contentProgress, setContentProgress] = useState({
        completedCount: 0,
        totalCount: 0,
        progressPercent: 0,
        stepIndex: 0,
        currentSession: 0,
        sessionProgress: 0
    });


    useEffect(() => {
        const fetchProgramData = async () => {
            try {
                if (!programData && code) {
                    setLoading(true);
                    const response = await getInsiderProgramService(code);
                    if (response?.data?.learning_program) {
                        setProgramData(response.data.learning_program);
                    }
                }
            } catch (error) {
                console.error('Error fetching program data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProgramData().then(r => console.log(r));
    }, [programData, code]);

    useEffect(() => {
        const fetchAllStatuses = async () => {
            setLoading(true);
            setPreviewEnabled(false);
            try {
                // Outline status
                const outlineRes = await getLearningProgramPreviewStatusService(code);
                const isOutlineReady = outlineRes?.data?.is_outline_ready;
                const generationInitiated = outlineRes?.data?.generation_initiated;
                const progressStatus = outlineRes?.data?.progress_status; // 1..5
                const failureReason = outlineRes?.data?.error || '';
                const failed = progressStatus === -1;
                const mlServerResp = outlineRes?.data?.ml_server_resp;

                setOutlineStatus({
                    is_outline_ready: isOutlineReady,
                    generation_initiated: generationInitiated,
                    failed,
                    failure_reason: failureReason,
                    ml_server_resp: mlServerResp
                });

                // Drive UI from API
                if (failed) {
                    setOutlineStepIndex(0);
                    setOutlineProgress(0);
                } else {
                    setOutlineStepIndex(isOutlineReady ? OUTLINE_STEPS.length : deriveStepIndexFromStatus(progressStatus));
                    setOutlineProgress(derivePercentFromStatus(isOutlineReady, progressStatus));
                }

                // Content generation status
                if (generationInitiated) {
                    const contentRes = await getLearningProgramStatusService(code);
                    const contentStatusData = contentRes?.data?.contentStatus || {};
                    const contentMlServerResp = contentRes?.data?.ml_server_resp;

                    setContentStatus({
                        ml_server_resp: contentMlServerResp
                    });

                    const progress = calculateContentProgress(contentStatusData);
                    setContentProgress(progress);

                    // Enable preview based on feature flag or full completion
                    if (ENABLE_EARLY_PREVIEW) {
                        const m1u1s1Data = contentStatusData['M1_U1_S1'];
                        const isM1U1S1Done = typeof m1u1s1Data === 'object'
                            ? m1u1s1Data.status === 'done'
                            : m1u1s1Data === 'done';
                        const hasMinimumProgress = progress.progressPercent >= 50;
                        setPreviewEnabled(isM1U1S1Done && hasMinimumProgress);
                    } else {
                        setPreviewEnabled(progress.progressPercent === 100);
                    }
                }
            } catch (e) {
                // Handle error
            } finally {
                setLoading(false);
            }
        };

        if (code) fetchAllStatuses();
    }, [code]);

    // Polling effect
    useEffect(() => {
        let intervalId;
        if (code && !outlineStatus.failed && (outlineStatus.is_outline_ready !== true || outlineStatus.generation_initiated !== true)) {
            intervalId = setInterval(async () => {
                const response = await getLearningProgramPreviewStatusService(code);
                const isReady = response?.data?.is_outline_ready;
                const initiated = response?.data?.generation_initiated;
                const progressStatus = response?.data?.progress_status; // 1..5
                const failureReason = response?.data?.error || '';
                const mlServerResp = response?.data?.ml_server_resp;

                // Failure: mark failed and stop polling
                if (progressStatus === -1) {
                    setOutlineStatus({
                        is_outline_ready: isReady,
                        generation_initiated: initiated,
                        failed: true,
                        failure_reason: failureReason,
                        ml_server_resp: mlServerResp
                    });
                    setOutlineStepIndex(0);
                    setOutlineProgress(0);
                    clearInterval(intervalId);
                    return;
                }

                setOutlineStatus({
                    is_outline_ready: isReady,
                    generation_initiated: initiated,
                    failed: false,
                    failure_reason: '',
                    ml_server_resp: mlServerResp
                });

                setOutlineStepIndex(
                    isReady ? OUTLINE_STEPS.length : deriveStepIndexFromStatus(progressStatus)
                        );
                setOutlineProgress(
                    derivePercentFromStatus(isReady, progressStatus)
                    );
                if (response?.data?.is_outline_ready && response?.data?.generation_initiated) {
                    clearInterval(intervalId);
                }
            }, 5000);
        }
        return () => clearInterval(intervalId);
    }, [code, outlineStatus.is_outline_ready, outlineStatus.generation_initiated, outlineStatus.failed]);

    // Polling effect for content generation
    useEffect(() => {
        let intervalId;

        if (
            outlineStatus.generation_initiated &&
            !outlineStatus.failed &&
            !previewEnabled // Only poll if not yet enabled
        ) {
            intervalId = setInterval(async () => {
                try {
                    const contentRes = await getLearningProgramStatusService(code);
                    const contentStatusData = contentRes?.data?.contentStatus || {};
                    const contentMlServerResp = contentRes?.data?.ml_server_resp;

                    setContentStatus({
                        ml_server_resp: contentMlServerResp
                    });

                    const progress = calculateContentProgress(contentStatusData);
                    setContentProgress(progress);

                    // Enable preview based on feature flag or full completion
                    if (ENABLE_EARLY_PREVIEW) {
                        const m1u1s1Data = contentStatusData['M1_U1_S1'];
                        const isM1U1S1Done = typeof m1u1s1Data === 'object'
                            ? m1u1s1Data.status === 'done'
                            : m1u1s1Data === 'done';
                        const hasMinimumProgress = progress.progressPercent >= 50;
                        if (isM1U1S1Done && hasMinimumProgress) {
                            setPreviewEnabled(true);
                            clearInterval(intervalId);
                        }
                    } else {
                        if (progress.progressPercent === 100) {
                            setPreviewEnabled(true);
                            clearInterval(intervalId);
                        }
                    }
                } catch (e) {
                    console.error('Error polling content status:', e);
                }
            }, 10000);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [code, outlineStatus.generation_initiated, previewEnabled, outlineStatus.failed]);

    // Check if outline has error based on ml_server_resp
    const hasOutlineError = () => {
        if (outlineStatus.ml_server_resp) {
            const mlResp = outlineStatus.ml_server_resp;
            return mlResp.error === true || (mlResp.http_code && mlResp.http_code !== 200) || (mlResp.status_code && mlResp.status_code !== 200);
        }
        return false;
    };

    // Check if content generation has error based on ml_server_resp
    const hasContentError = () => {
        if (contentStatus.ml_server_resp) {
            const mlResp = contentStatus.ml_server_resp;
            return mlResp.error === true || (mlResp.http_code && mlResp.http_code !== 200) || (mlResp.status_code && mlResp.status_code !== 200);
        }
        return false;
    };

    // Handler to rebuild the outline if not generated yet
    const handleReBuildOutline = async () => {
        const request_type = 'retry';
        setLoading(true);
        try {
            const response = await buildLearningProgramService(
                code,
                programData.full_name,
                programData.target_audience,
                programData.dq_codes,
                programData.time_to_complete_minutes,
                programData.image_url,
                programData.theme_color,
                programData.badge_background,
                programData.primary_color,
                programData.description,
                programData.more_info,
                programData.translations,
                programData.program_details,
                request_type,
            );

            // Reset outline state to restart polling
            setOutlineStatus({
                is_outline_ready: false,
                generation_initiated: false,
                failed: false,
                failure_reason: '',
                ml_server_resp: null
            });
            setOutlineStepIndex(0);
            setOutlineProgress(0);
        } catch (error) {
            console.error('Error building outline:', error);
            toastWarning('Failed to build program outline');
        } finally {
            setLoading(false);
        }
    };

    // Handler to regenerate program content if outline is ready but content not generated
    const handleReGenerateContent = async () => {
        const request_type = 'retry';
        setLoading(true);
        try {
            const response = await generateLearningProgramContentService(code, request_type);

            // Reset content generation state to restart polling
            setPreviewEnabled(false);
            setContentProgress({
                completedCount: 0,
                totalCount: 0,
                progressPercent: 0,
                stepIndex: 0,
                currentSession: 0,
                sessionProgress: 0
            });
            setContentStatus({
                ml_server_resp: null
            });
        } catch (error) {
            console.error('Error generating program content:', error);
            toastWarning('Failed to generate program content');
        } finally {
            setLoading(false);
        }
    };

    // Determine button labels and actions based on current state
    const getOutlineButtonConfig = () => {
        if (hasOutlineError() || outlineStatus.failed) {
            return { label: 'Retry', action: handleReBuildOutline };
        }
        return { label: 'View/Edit', url: `/dq_pro/products/program/${code}/outline` };
    };

    const getContentButtonConfig = () => {
        if (hasContentError()) {
            return { label: 'Retry', action: handleReGenerateContent };
        }
        return { label: 'Preview', url: `/dq_pro/products/program/${code}/preview` };
    };

    const cardsData = [
        {
            icon: Icon1,
            title: 'Program Outline',
            content: 'Overview of your curated program',
            buttons: [getOutlineButtonConfig()],
            height: '40px',
            width: '70px'
        },
        {
            icon: Icon2,
            title: 'Your Program',
            content: 'Preview your ready-to-go online program',
            buttons: [getContentButtonConfig()],
            height: '45px',
            width: '55px'
        },
        {
            icon: Icon4,
            title: 'Resources',
            content: 'Everything you need to run your program online, offline, or integrate with your system via API',
            buttons: [
                {
                    label: 'Access',
                    url: `/dq_pro/products/program/${code}/resources`
                }
            ],
            height: '45px',
            width: '52px'
        }
    ];

    const activeStep = 2;

    return (
        <div className={classes.container}>
            <Typography variant="h4" className={classes.title}>
                Here’s your program!
            </Typography>
            <Box sx={{flexDirection:{xs:'column', sm:'row'}, padding: {xs:'0px',md:'0px',lg:'0px 110px'}, alignItems:{xs:'center',}, }} style={{ display: 'flex', justifyContent: 'space-around', }}>
                {cardsData.map((step, index) => {
                    let showLoader = false;
                    let disabled = true;
                    let hasError = false;

                    if (index === 0) { // Program Outline
                        hasError = hasOutlineError() || outlineStatus.failed;
                        showLoader = !hasError && outlineStatus.is_outline_ready !== true;
                        // Enable button if there's an error (for regeneration) or if outline is ready
                        disabled = !hasError && outlineStatus.is_outline_ready !== true;
                    } else if (index === 1) { // Program Preview
                        hasError = hasContentError();
                        showLoader = !hasError && outlineStatus.generation_initiated && !previewEnabled;
                        // Enable button if there's an error (for regeneration) or if preview is enabled
                        disabled = !hasError && (!previewEnabled || outlineStatus.is_outline_ready !== true);
                    } else if (index === 2) { // Resources
                        // Resources card is enabled only when Program Preview (2nd card) is enabled
                        disabled = !previewEnabled || outlineStatus.is_outline_ready !== true;
                    }
                    return (
                    <Card
                        key={index}
                        className={`${classes.card}`}
                        style={{
                            pointerEvents: index <= activeStep ? 'auto' : 'none',
                            position: 'relative',
                                transition: 'opacity 0.3s ease, filter 0.3s ease'
                        }}
                    >
                            {(disabled || showLoader || hasError) && (
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backgroundColor: hasError ? 'rgba(255, 235, 238, 0.41)' : 'rgba(202,199,199,0.41)',
                                backdropFilter: 'blur(2px)',
                                zIndex: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 1,
                                filter: 'none'
                            }} />
                        )}

                        {/* Loader overlay */}
                        {showLoader && !hasError && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: showLoader ? 'rgba(202,199,199,0.7)' : 'rgba(202,199,199,0.41)',
                                    backdropFilter: 'blur(2px)',
                                    zIndex: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 1,
                                    filter: 'none'
                                }}
                            >
                                    {showLoader && (
                                        <>
                                            <CircularProgress sx={{ color: '#ff6468' }}/>
                                            {index === 0 ? (
                                                <>
                                                    {/* Active step only */}
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            mt: 1,
                                                            color: outlineStatus.is_outline_ready ? '#2e7d32' : '#ff6468',
                                                            fontWeight: 700,
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        {outlineStatus.is_outline_ready
                                                            ? 'Outline ready. Finalizing…'
                                                            : OUTLINE_STEPS[Math.min(outlineStepIndex, OUTLINE_STEPS.length - 1)]
                                                        }
                                                    </Typography>
                                                    <Box sx={{ width: '80%', mb: 1 }}>
                                                        <LinearProgress variant="determinate" value={outlineProgress} sx={{ height: 10, borderRadius: 5 }} />
                                                    </Box>
                                                    <Typography variant="caption" sx={{ mb: 2, color: '#333', fontWeight: 600 }}>
                                                        {Math.round(outlineProgress)}%
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            mt: 2,
                                                            color: '#ff6468',
                                                            fontWeight: 700,
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        {CONTENT_GENERATION_STEPS[Math.min(contentProgress.stepIndex, CONTENT_GENERATION_STEPS.length - 1)]}
                                                    </Typography>
                                                    <Box sx={{ width: '80%', mt: 2, mb: 1 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={contentProgress.progressPercent}
                                                            sx={{ height: 10, borderRadius: 5 }}
                                                        />
                                                    </Box>
                                                    <Typography variant="caption" sx={{ mb: 1, color: '#333', fontWeight: 600 }}>
                                                        {contentProgress.progressPercent}% ({contentProgress.completedCount}/{contentProgress.totalCount} sessions completed)
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            mt: 1,
                                                            color: '#666',
                                                            textAlign: 'center',
                                                            fontSize:'11px',
                                                            maxWidth: '85%',
                                                        }}
                                                    >
                                                        (Take note that images for your content may take an additional few minutes to appear in the program slides after the text has been generated.)
                                                    </Typography>
                                                </>
                                            )}
                                        </>
                                    )}
                            </Box>
                        )}

                        {/* Error overlay - covers card except button area at bottom */}
                        {hasError && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: 'calc(100% - 70px)', // Leave space for button at bottom
                                    backgroundColor: 'rgba(255, 235, 238, 0.95)',
                                    zIndex: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 3,
                                    textAlign: 'center'
                                }}
                            >
                                <Typography variant="h6" sx={{ color: '#c62828', fontWeight: 800, mb: 1 }}>
                                    {index === 0 ? 'Outline generation failed' : 'Content generation failed'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#b71c1c', mb: 2 }}>
                                    Something went wrong. Please try again.
                                </Typography>
                                {index === 0 && outlineStatus.failure_reason && (
                                    <Typography variant="caption" sx={{ color: '#d32f2f', fontStyle: 'italic' }}>
                                        {outlineStatus.failure_reason}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <CardContent className={classes.cardContent}>
                            <div className={classes.cardHeader}>
                                <img src={step.icon} alt={step.title}
                                     style={{width: step.width, height: step.height, marginBottom: '20px', marginTop: '20px'}}/>
                                <Typography variant="h5"
                                            style={{color: '#fff', marginBottom: '10px'}}>{step.title}</Typography>
                            </div>
                            <Typography variant="body1" style={{
                                display: index === 0 && showLoader ? 'none' : 'block',
                                marginTop: '50px',
                                padding: '0 10px',
                                textAlign: 'center'
                            }}>{step.content}</Typography>

                            {index <= activeStep && (
                                <Box sx={{flexDirection:{sm:'row', xs:'column'}, zIndex: hasError ? 4 : 1}} style={{display: 'flex', position: 'absolute', bottom: '25px', width:'95%',}}>
                                    {step.buttons.map((button, btnIndex) => (
                                            <Button
                                                key={btnIndex}
                                                className={classes.button}
                                                variant="contained"
                                                onClick={() => {
                                                    if (button.action) {
                                                        button.action();
                                                    } else if (button.onClick) {
                                                        button.onClick();
                                                    } else if (button.url) {
                                                        history.push({
                                                            pathname: button.url,
                                                            state: {
                                                                assessment: programData,
                                                                outlineStatus: outlineStatus,
                                                                dqcoList: dqcoList
                                                            }
                                                        });
                                                    }
                                                }}
                                                disabled={hasError ? false : disabled}
                                            >
                                                {button.label}
                                            </Button>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                    )
                })}
            </Box>
            <LoaderWithBackDrop loading={loading} />
        </div>
    );
};

export default DQProProgDashboardPage;